/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Button from '@ba-ui-toolkit/button';
import SVGIcon from '@ba-ui-toolkit/svgicon';
import Tags from '@ba-ui-toolkit/tags';
import TruncatedText from '@ba-ui-toolkit/truncatedtext';
import FlexLayout, { FlexItem } from '@ba-ui-toolkit/flexlayout';
import ToolTip from '@ba-ui-toolkit/tooltip';
import menuOverflow from '@ba-ui-toolkit/ba-graphics/dist/icons-js/overflow-menu--horizontal_32';
import verticalMenuOverflow from '@ba-ui-toolkit/ba-graphics/dist/icons-js/overflow-menu--horizontal_16';
import folder16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/folder_16';
import { classnames as cx, propsUtils } from '@ba-ui-toolkit/utils';
import { Tile as CarbonTile } from 'carbon';
import styles from './Tile.scss';

const { sanitize } = propsUtils;
const tileClassName = styles.tileClassName.replace(/"/g, '');

export default class Tile extends Component {
	static propTypes = {
		/** Custom class name(s) */
		className: PropTypes.string,
		/** Select large or small size of the tile */
		size: PropTypes.string,
		/** Determines if the tile is an asset or a container (folder) */
		container: PropTypes.bool,
		/** Flag to select the tile */
		selected: PropTypes.bool,
		/** Data passed to tile */
		content: PropTypes.shape({
			type: PropTypes.string,
			iconId: PropTypes.string,
			label: PropTypes.string,
			date: PropTypes.string,
			color: PropTypes.string,
			value: PropTypes.any,
		}).isRequired,
		/** Callback function for context menu */
		onMenu: PropTypes.func,
		/** Svg icon when loading context menu */
		ellipsisIcon: PropTypes.element,
		ellipsisTooltipLabel: PropTypes.string,
		dateLabel: PropTypes.string,
		/** Text direction */
        textDir: PropTypes.oneOf(['', 'ltr', 'rtl', 'auto']),
	};

	static defaultProps = {
		size: 'small',
		container: false,
		selected: false,
		className: '',
		ellipsisTooltipLabel: 'Action menu',
		dateLabel: 'Last modified',
	};

	_onContextMenu = e => {
		const { onMenu } = this.props;
		onMenu && onMenu(e);
	};

	_ref = node => {
		this._buttonNode = node;
	};

	_menuRenderer() {
		const { size, ellipsisIcon } = this.props;
		if (size === 'small') {
			return (
				<ToolTip
					className={`${tileClassName}__footer__title`}
					orient="bottom"
					title={this.props.ellipsisTooltipLabel}
					delay
				>
					{ellipsisIcon || (
						<Button
							className={`${tileClassName}__footer__icon`}
							ref={this._ref}
							onClick={this._onContextMenu}
							variant="inline"
							iconSize="normal"
							icon={menuOverflow.id}
						/>
					)}
				</ToolTip>
			);
		}

		return (
			<ToolTip
				className={`${tileClassName}__header__title`}
				orient="bottom"
				title={this.props.ellipsisTooltipLabel}
				delay
			>
				{ellipsisIcon || (
					<Button
						className={`${tileClassName}__header__icon`}
						ref={this._ref}
						onClick={this._onContextMenu}
						variant="inline"
						iconSize="small"
						icon={verticalMenuOverflow.id}
					/>
				)}
			</ToolTip>
		);
	}

	render() {
		const {
			container,
			content,
			className,
			selected,
			onMenu,
			ellipsisIcon,
			size,
			textDir,
			dateLabel,
			...others
		} = this.props;
		const classNames = cx(tileClassName, size, {
			selected,
			container,
		});
		if (container) {
			return (
				<CarbonTile>
					<FlexLayout
						direction="row"
						alignItems="center"
						{...sanitize(others, Tile)}
						tabIndex="0"
						className={`${classNames} ${className}`}
					>
						<FlexItem className={`${tileClassName}__left`}>
							<SVGIcon
								className={`${tileClassName}__left__icon`}
								iconId={content.iconId || folder16.id}
							/>
						</FlexItem>
						<FlexItem grow shrink>
							<FlexLayout
								direction="column"
								justifyContent="center"
								className={`${tileClassName}__middle`}
							>
								<FlexItem>
									<TruncatedText
										className={`${tileClassName}__middle__label`}
										width="100%"
										value={content.label}
										textDir={textDir}
									/>
								</FlexItem>
								<FlexItem>
									<div className={`${tileClassName}__middle__date`}>
										{content.date}
									</div>
								</FlexItem>
							</FlexLayout>
						</FlexItem>
						<FlexItem className={`${tileClassName}__right`}>
							{(onMenu || ellipsisIcon) && this._menuRenderer()}
						</FlexItem>
					</FlexLayout>
				</CarbonTile>
			);
		}
		if (size === 'large') {
			const presetColors = [
				'red',
				'magenta',
				'purple',
				'blue',
				'cyan',
				'teal',
				'green',
				'gray',
				'coolgray',
				'warmgray',
			];
			const tagColor =
				presetColors.indexOf(content.color) !== -1 ? content.color : 'coolgray';

			return (
				<CarbonTile>
					<FlexLayout
						direction="column"
						{...sanitize(others, Tile)}
						tabIndex="0"
						onContextMenu={onMenu ? this._onContextMenu : null}
						className={`${classNames} ${className}`}
					>
						<FlexItem>
							<FlexLayout
								alignItems="center"
								justifyContent="space-between"
								className={`${tileClassName}__header`}
							>
								<ToolTip
									className={`${tileClassName}__icon__title`}
									orient="top"
									title={content.type}
									delay
								>
									<SVGIcon
										className={`${tileClassName}__header__type`}
										iconId={content.iconId}
										title={content.type}
										size="large"
									/>
								</ToolTip>
								<FlexItem>
									{(onMenu || ellipsisIcon) && this._menuRenderer()}
								</FlexItem>
							</FlexLayout>
						</FlexItem>
						<FlexItem grow shrink className={`${tileClassName}__body`}>
							<TruncatedText
								className={`${tileClassName}__body__label`}
								tooltipDelay
								title={content.label}
								width="100%"
								textDir={textDir}
								style={{ maxWidth: '100%' }}
								value={content.label}
							/>
							<FlexLayout
								direction="row"
								justifyContent="space-between"
								alignItems="center"
								className={`${tileClassName}__body__tag`}
							>
								<ToolTip orient="right" title={content.type} delay>
									<Tags
										tags={[
											{
												value: content.type,
												label: content.type,
												color: tagColor,
											},
										]}
										removable={false}
									/>
								</ToolTip>
							</FlexLayout>
						</FlexItem>
						<FlexItem>
							<FlexItem className={`${tileClassName}__footer__date`}>
								{dateLabel}
								<br />
								<TruncatedText
									tooltipDelay
									title={content.date}
									width="100%"										
									value={content.date}
								/>
							</FlexItem>
						</FlexItem>
					</FlexLayout>
				</CarbonTile>
			);
		}

		return (
			<CarbonTile>
				<FlexLayout
					direction="column"
					{...sanitize(others, Tile)}
					tabIndex="0"
					onContextMenu={onMenu ? this._onContextMenu : null}
					className={`${classNames} ${className}`}
				>
					<FlexItem>
						<FlexLayout
							direction="row"
							alignItems="center"
							className={`${tileClassName}__header`}
						>
							<ToolTip
								className={`${tileClassName}__icon__title`}
								orient="top"
								title={content.type}
								delay
							>
								<SVGIcon
									className={`${tileClassName}__header__icon`}
									iconId={content.iconId}
									title={content.type}
								/>
							</ToolTip>
							<TruncatedText
								tooltipDelay
								title={content.type}
								width="100%"
								value={content.type}
							/>
						</FlexLayout>
					</FlexItem>
					<FlexItem grow shrink className={`${tileClassName}__body`}>
						<TruncatedText
							tooltipDelay
							title={content.label}
							width="100%"
							textDir={textDir}
							value={content.label}
						/>
					</FlexItem>
					<FlexItem>
						<FlexLayout
							alignItems="center"
							justifyContent="space-between"
							className={`${tileClassName}__footer`}
						>
							<ToolTip
								className={`${tileClassName}__date__title`}
								orient="bottom"
								title={content.date}
								delay
							>
								<FlexItem>{content.date}</FlexItem>
							</ToolTip>
							<FlexItem>{(onMenu || ellipsisIcon) && this._menuRenderer()}</FlexItem>
						</FlexLayout>
					</FlexItem>
				</FlexLayout>
			</CarbonTile>
		);
	}
}
